/**
 * CHAINWARE — Theme JS
 *
 * Handles:
 *  1. Mobile nav toggle
 *  2. Variant selector logic + Phase 2 event dispatch
 *  3. Add-to-cart feedback
 *  4. Phase 2 hook: listens for chainware:variantChange to swap
 *     between standard purchase UI and 3D customizer launch CTA.
 */

(function () {
  'use strict';

  /* ── MOBILE NAV ──────────────────────────────────────── */
  function initNav() {
    const toggle = document.querySelector('.nav-toggle');
    const links  = document.querySelector('.nav-links');
    if (!toggle || !links) return;

    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      const open = links.classList.toggle('open', !expanded);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.site-header')) {
        toggle.setAttribute('aria-expanded', 'false');
        links.classList.remove('open');
      }
    });
  }

  /* ── VARIANT SELECTOR ────────────────────────────────── */
  /**
   * Dispatches a custom `chainware:variantChange` event on document.
   *
   * Event detail shape:
   *   {
   *     variantId:    string   — Shopify variant ID
   *     variantTitle: string   — Human-readable title (e.g. "Standard")
   *     variantType:  string   — data-variant-type value: "standard" | "personalized"
   *     isPersonalized: boolean
   *   }
   *
   * Phase 2 integration: Listen for `chainware:variantChange` and,
   * when isPersonalized === true, load the external Vercel 3D customizer app
   * using the model URL from the data attribute on the product form:
   *   document.getElementById('chainware-product-form')?.dataset.modelUrl
   * (maps to product.metafields.custom.3d_model_url)
   */
  function initVariantSelector() {
    const form        = document.getElementById('chainware-product-form');
    const select      = document.getElementById('chainware-variant-select');
    const atcBtn      = document.getElementById('chainware-atc-btn');
    const customizerCta = document.getElementById('chainware-customizer-cta');

    if (!select) return;

    function applyVariantChange(option) {
      const variantType  = option.dataset.variantType || 'standard';
      const isPersonalized = variantType === 'personalized';

      // Dispatch clean change event for Phase 2 listeners
      document.dispatchEvent(new CustomEvent('chainware:variantChange', {
        bubbles: true,
        detail: {
          variantId:      option.value,
          variantTitle:   option.textContent.trim(),
          variantType,
          isPersonalized,
          modelUrl:       document.getElementById('chainware-model-url')?.value || null,
        },
      }));

      // Toggle standard purchase UI vs customizer CTA
      const standardEls    = document.querySelectorAll('[data-standard-purchase]');
      const customizerEls  = document.querySelectorAll('[data-customizer-purchase]');

      standardEls.forEach(el => { el.hidden = isPersonalized; });
      customizerEls.forEach(el => { el.hidden = !isPersonalized; });

      // Reflect availability on ATC button
      if (atcBtn && !isPersonalized) {
        const available = option.dataset.available !== 'false';
        atcBtn.disabled = !available;
        atcBtn.textContent = available ? 'Add to Cart' : 'Sold Out';
      }
    }

    // Initialise on page load with the pre-selected option
    const selected = select.options[select.selectedIndex];
    if (selected) applyVariantChange(selected);

    select.addEventListener('change', (e) => {
      const option = e.target.options[e.target.selectedIndex];
      applyVariantChange(option);
    });
  }

  /* ── ADD TO CART FORM ────────────────────────────────── */
  /**
   * Graceful ATC: intercepts the form submit to give feedback.
   * Shopify's native Ajax Cart API can slot in here in Phase 2.
   */
  function initAddToCart() {
    const form  = document.getElementById('chainware-product-form');
    const btn   = document.getElementById('chainware-atc-btn');
    if (!form || !btn) return;

    form.addEventListener('submit', (e) => {
      if (btn.disabled) { e.preventDefault(); return; }

      // Optimistic feedback — Shopify handles actual cart add via native submit
      const original = btn.innerHTML;
      btn.textContent = 'Adding…';
      btn.disabled = true;

      setTimeout(() => {
        btn.innerHTML = original;
        btn.disabled = false;
      }, 1800);
    });
  }


  /* ── PRODUCT IMAGE GALLERY ───────────────────────────── */
  /**
   * Click-through gallery: click a thumbnail to swap the main image.
   * Arrow keys navigate between thumbs when one is focused.
   * Slides + thumbs share data-index for pairing.
   */
  function initGallery() {
    const slides = document.querySelectorAll('.gallery-slide');
    const thumbs = document.querySelectorAll('.gallery-thumb');
    if (!slides.length || !thumbs.length) return;

    function activate(index) {
      slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
      thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
        t.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
    }

    const thumbArr = Array.from(thumbs);
    thumbArr.forEach((thumb, i) => {
      thumb.addEventListener('click', () => activate(i));
      thumb.addEventListener('keydown', (e) => {
        let next = -1;
        if (e.key === 'ArrowRight') next = (i + 1) % thumbArr.length;
        if (e.key === 'ArrowLeft')  next = (i - 1 + thumbArr.length) % thumbArr.length;
        if (next >= 0) {
          e.preventDefault();
          thumbArr[next].focus();
          activate(next);
        }
      });
    });
  }

  /* ── PHASE 2 CUSTOMIZER HOOK (stub) ──────────────────── */
  /**
   * This listener is intentionally left minimal.
   * In Phase 2, replace the console.info with:
   *   1. Hide the standard product form
   *   2. Load the Vercel customizer iframe/app using detail.modelUrl
   *   3. Pass the selected variantId to the customizer
   */
  document.addEventListener('chainware:variantChange', (e) => {
    if (e.detail.isPersonalized) {
      console.info('[CHAINWARE] Personalized variant selected — Phase 2 customizer will load here.', e.detail);
    }
  });

  /* ── INIT ────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', () => {
    initNav();
    initVariantSelector();
    initAddToCart();
    initGallery();
  });

})();
